import React from 'react';
import { AGENTS } from '../context/AgentContext';

const STATUS_COLORS = {
  completed: '#10b981',
  running: '#3b82f6',
  failed: '#ef4444',
  pending: '#374151',
};

const AgentRing = ({ pipeline, size = 96 }) => {
  if (!pipeline) return null;

  const center = size / 2;
  const radius = (size / 2) - 8;
  const strokeWidth = 6;
  const gap = 0.04; // gap between segments in radians
  const totalAngle = 2 * Math.PI;
  const segmentAngle = (totalAngle - gap * AGENTS.length) / AGENTS.length;

  const completedCount = AGENTS.filter(a => pipeline[a.id]?.status === 'completed').length;
  const activeAgent = AGENTS.find(a => pipeline[a.id]?.status === 'running');
  const allDone = completedCount === AGENTS.length;

  const polarToCartesian = (cx, cy, r, angle) => ({
    x: cx + r * Math.cos(angle - Math.PI / 2),
    y: cy + r * Math.sin(angle - Math.PI / 2),
  });

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, startAngle);
    const end = polarToCartesian(cx, cy, r, endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  let centerText = 'Ready';
  let centerSubtext = `${completedCount}/4`;
  if (allDone) {
    centerText = 'Done';
    centerSubtext = '';
  } else if (activeAgent) {
    centerText = activeAgent.label;
    centerSubtext = 'running';
  }

  return (
    <div className="agent-ring-container" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#1f2937"
          strokeWidth={strokeWidth + 2}
          opacity="0.5"
        />

        {/* Agent segments */}
        {AGENTS.map((agent, i) => {
          const startAngle = i * (segmentAngle + gap);
          const endAngle = startAngle + segmentAngle;
          const status = pipeline[agent.id]?.status || 'pending';
          const color = STATUS_COLORS[status];
          const isRunning = status === 'running';

          return (
            <path
              key={agent.id}
              d={describeArc(center, center, radius, startAngle, endAngle)}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              className={isRunning ? 'agent-ring-segment-running' : ''}
              style={{
                filter: isRunning ? `drop-shadow(0 0 4px ${color})` : 'none',
                opacity: status === 'pending' ? 0.4 : 1,
              }}
            />
          );
        })}

        {/* Center text */}
        <text
          x={center}
          y={centerSubtext ? center - 4 : center + 1}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={allDone ? '#10b981' : activeAgent ? '#3b82f6' : '#9ca3af'}
          fontSize={size < 80 ? '10' : '12'}
          fontFamily="'JetBrains Mono', 'SF Mono', monospace"
          fontWeight="600"
          letterSpacing="0.5"
        >
          {centerText}
        </text>
        {centerSubtext && (
          <text
            x={center}
            y={center + (size < 80 ? 10 : 12)}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#6b7280"
            fontSize={size < 80 ? '8' : '9'}
            fontFamily="'JetBrains Mono', 'SF Mono', monospace"
          >
            {centerSubtext}
          </text>
        )}
      </svg>
    </div>
  );
};

export default AgentRing;
